import { Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { filter, map, startWith } from 'rxjs';

import { injectLeafRouteData } from '@app/core/layouts/leaf-route-data';
import { SidebarPageLayoutComponent } from '@app/core/layouts/sidebar-page-layout/sidebar-page-layout.component';

import { AccessProfileService } from '@core/services/access-profile.service';
import { AuthSessionService } from '@core/services/auth-session.service';

import { MenuItem } from '@shared/components/sidebar-menu/sidebar-menu.component';

@Component({
  selector: 'mw-settings-layout',
  standalone: true,
  imports: [RouterOutlet, SidebarPageLayoutComponent],
  templateUrl: './settings-layout.component.html',
  styleUrl: './settings-layout.component.scss'
})
export class SettingsLayoutComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthSessionService);
  private readonly accessProfile = inject(AccessProfileService);

  private readonly isHighAccess = toSignal(this.auth.isHighAccess$);
  private readonly isManager = toSignal(this.accessProfile.isManager$);

  private readonly leafRouteData = injectLeafRouteData();

  protected readonly fixedHeight = computed(
    () => this.leafRouteData()['fixedHeight'] === true
  );

  private readonly isGeneralSettings = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url.includes('/configuracoes-gerais')),
      startWith(this.router.url.includes('/configuracoes-gerais'))
    ),
    { initialValue: this.router.url.includes('/configuracoes-gerais') }
  );

  private readonly adminMenuItems = computed<MenuItem[]>(() => [
    {
      key: 'product-management',
      text: 'Produtos',
      disabled: !this.isHighAccess(),
      subItems: [
        {
          key: 'register-product',
          icon: 'lucideBookOpenCheck',
          text: 'Produtos cadastrados',
          route: '/configuracoes/gestao-de-produtos'
        },
        {
          key: 'list-products',
          icon: 'lucideSquarePen',
          text: 'Editar produtos por escola',
          route: '/configuracoes/gestao-de-produtos-escola'
        },
        {
          key: 'terms-management',
          icon: 'lucideFileText',
          text: 'Gestão de termos',
          route: '/configuracoes/gestao-de-termos',
          isNew: '2026-12-01'
        }
      ]
    },
    {
      key: 'school-products-management',
      text: 'Envio para a gráfica',
      subItems: [
        {
          key: 'register-school-products',
          icon: 'lucidePackage',
          text: 'Pacotes de envio',
          route: '/configuracoes/pacotes-de-envio'
        }
      ]
    }
  ]);

  private readonly generalMenuItems = computed<MenuItem[]>(() => [
    {
      key: 'school-data',
      text: 'Dados da Escola',
      subItems: [
        {
          key: 'school-info',
          icon: 'lucideSchool',
          text: 'Informações da escola',
          route: '/configuracoes-gerais/informacoes-da-escola',
          disabled: !this.isManager()
        },
        {
          key: 'teacher-biography',
          icon: 'lucideFilePenLine',
          text: 'Biografia do professor',
          route: '/configuracoes-gerais/biografia-do-professor'
        },
        {
          key: 'student-info',
          icon: 'lucideUserPen',
          text: 'Dados dos alunos',
          route: '/configuracoes-gerais/dados-dos-alunos'
        },
        {
          key: 'class-info',
          icon: 'lucideUsers',
          text: 'Dados da turma',
          route: '/configuracoes-gerais/dados-da-turma'
        }
      ]
    }
  ]);

  readonly menuItems: Signal<MenuItem[]> = computed(() =>
    this.isGeneralSettings() ? this.generalMenuItems() : this.adminMenuItems()
  );
}
